import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Sons d'alerte
const ALERT_SOUNDS = {
  urgent: '/sounds/alert-urgent.wav',
  important: '/sounds/alert-important.wav'
};

const SecurityNotificationsContext = createContext(null);

export function useSecurityNotifications() {
  const context = useContext(SecurityNotificationsContext);
  if (!context) {
    throw new Error('useSecurityNotifications must be used within SecurityNotificationsProvider');
  }
  return context;
}

export function SecurityNotificationsProvider({ children }) {
  const { token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [scannerStatus, setScannerStatus] = useState(null);
  
  const audioRefs = useRef({
    urgent: null,
    important: null
  });
  const lastCheckRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Vérifier le support des notifications
  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
    
    // Précharger les sons
    audioRefs.current.urgent = new Audio(ALERT_SOUNDS.urgent);
    audioRefs.current.important = new Audio(ALERT_SOUNDS.important);
    
    // Précharger
    audioRefs.current.urgent.load();
    audioRefs.current.important.load();
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Enregistrer le Service Worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return false;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw-security.js');
      console.log('Security Service Worker registered:', registration);
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }, []);

  // Demander la permission des notifications
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        await registerServiceWorker();
        toast.success('Notifications activées');
        return true;
      } else {
        toast.error('Notifications refusées');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported, registerServiceWorker]);

  // Jouer un son d'alerte
  const playAlertSound = useCallback((priority) => {
    const audio = audioRefs.current[priority] || audioRefs.current.important;
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(e => console.log('Audio play blocked:', e));
    }
  }, []);

  // Afficher une notification native
  const showNativeNotification = useCallback((notification) => {
    if (permission !== 'granted') return;
    
    const options = {
      body: notification.message,
      icon: '/logo192.png',
      badge: '/badge.png',
      tag: notification.id,
      requireInteraction: notification.priority === 'urgent',
      data: {
        url: '/security',
        alertId: notification.id
      }
    };
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title: notification.title,
        options
      });
    } else {
      new Notification(notification.title, options);
    }
  }, [permission]);

  // Récupérer les notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/security/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Détecter les nouvelles notifications
        if (lastCheckRef.current && data.notifications.length > 0) {
          const newNotifications = data.notifications.filter(n => {
            const createdAt = new Date(n.created_at);
            return createdAt > lastCheckRef.current && !n.read;
          });
          
          // Afficher les nouvelles notifications
          for (const notif of newNotifications) {
            // Notification toast dans l'app
            if (notif.priority === 'urgent') {
              toast.error(notif.title, {
                description: notif.message,
                duration: 10000,
                action: {
                  label: 'Voir',
                  onClick: () => window.location.href = '/security'
                }
              });
              
              // Son urgent
              if (notif.play_sound) {
                playAlertSound('urgent');
              }
            } else {
              toast.warning(notif.title, {
                description: notif.message,
                duration: 5000
              });
              
              // Son important (si pas mode silencieux)
              if (notif.play_sound) {
                playAlertSound('important');
              }
            }
            
            // Notification native
            showNativeNotification(notif);
          }
        }
        
        lastCheckRef.current = new Date();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching security notifications:', error);
    }
  }, [token, playAlertSound, showNativeNotification]);

  // Récupérer le statut du scanner
  const fetchScannerStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/security/scanner/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setScannerStatus(await response.json());
      }
    } catch (error) {
      console.error('Error fetching scanner status:', error);
    }
  }, [token]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      await fetch(`${API}/api/security/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [token]);

  // Marquer toutes comme lues
  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API}/api/security/notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [token]);

  // Déclencher un scan manuel
  const triggerManualScan = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/security/scanner/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Scan de sécurité déclenché');
        // Rafraîchir après un délai
        setTimeout(fetchNotifications, 3000);
      }
    } catch (error) {
      toast.error('Erreur lors du scan');
    }
  }, [token, fetchNotifications]);

  // Polling des notifications (toutes les 30 secondes)
  useEffect(() => {
    if (!token || !user) return;
    
    // Vérifier si l'utilisateur a accès aux alertes de sécurité
    const allowedRoles = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'];
    if (!allowedRoles.includes(user.role)) return;
    
    // Fetch initial
    fetchNotifications();
    fetchScannerStatus();
    
    // Polling
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, 30000); // 30 secondes
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [token, user, fetchNotifications, fetchScannerStatus]);

  // Écouter les messages du Service Worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        window.location.href = event.data.url;
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleMessage);
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  const value = {
    notifications,
    unreadCount,
    isSupported,
    permission,
    scannerStatus,
    requestPermission,
    markAsRead,
    markAllAsRead,
    triggerManualScan,
    fetchNotifications,
    playAlertSound
  };

  return (
    <SecurityNotificationsContext.Provider value={value}>
      {children}
    </SecurityNotificationsContext.Provider>
  );
}
